/* What chat says for its common actions - word for word the same as the mobile app's
   (care_diary_app_rn/lib/chatCopy.ts). Change one, change both. Wording that
   only fits one platform (Tap / Click, Save to phone) stays where it's used. */

const plural = (n, one, many) => (n > 1 ? many : one);

/* ----- Deleting messages ----- */
export const DELETE_FOR_ME = {
  label: "Delete for me",
  hint: "Removed for you only",
  title: (n) => plural(n, "Delete for me?", `Delete ${n} messages for you?`),
  text: (n) =>
    plural(
      n,
      "It'll be removed for you only. Everyone else can still see it.",
      "They'll be removed for you only. Everyone else can still see them.",
    ),
  done: (n) => plural(n, "Message deleted for you", "Messages deleted for you"),
  failed: (failed, total) =>
    failed === total
      ? plural(
          total,
          "Couldn't delete that for you - please try again",
          `Couldn't delete those ${total} messages for you - please try again`,
        )
      : `${failed} of ${total} messages couldn't be deleted for you - please try again`,
};

export const DELETE_FOR_EVERYONE = {
  label: "Delete for everyone",
  hint: "Removed for all members",
  title: (n) => plural(n, "Delete for everyone?", `Delete ${n} messages for everyone?`),
  text: (n) =>
    plural(
      n,
      "It'll be removed for all members. This can't be undone.",
      "They'll be removed for all members. This can't be undone.",
    ),
  failed: (failed, total) =>
    failed === total
      ? plural(
          total,
          "Couldn't delete the message - please try again",
          `Couldn't delete those ${total} messages - please try again`,
        )
      : `Couldn't delete ${failed} of ${total} messages.`,
};

// Choosing who it's deleted for (several selected, or a message that's mine)
export const DELETE_CHOICE = {
  title: (n) => plural(n, "Delete message?", `Delete ${n} messages?`),
  text: "Choose who it's deleted for. This can't be undone.",
};

// A message that never went (still sending, or failed)
export const DELETE_UNSENT = {
  label: "Delete",
  title: (n) => plural(n, "Delete message?", `Delete ${n} messages?`),
  text: "It hasn't been confirmed as sent. If it already reached the server, it will still show as sent and you can delete it from its menu.",
};

/* ----- Selecting messages ----- */
export const SELECTION = {
  count: (n) => `${n} selected`,
  copied: "Copied",
  copiedTextOnly: "Copied. Attachments aren't copied.",
  onlyForMe: "Some of these aren't yours, so they can only be deleted for you.",
};

/* ----- Messages and files ----- */
export const MESSAGE_GONE = "This message is no longer available.";
// Going to a message that couldn't be loaded (offline, a server error) - not "gone": it may be there
export const MESSAGE_UNREACHABLE = "Couldn't load that message. Try again.";
/* ----- Replying: the message being replied to was deleted for everyone meanwhile. The server
   won't take a reply to it, so the reply has to be dropped (the draft stays) ----- */
export const REPLY = {
  unavailable: "Original message unavailable",
  goneOnSend: "The message you're replying to is no longer available. Remove it to send your message.",
};
export const MESSAGE_INFO = {
  label: "Message info",
  readBy: "Read by",
  notRead: "Not yet read",
  failed: "Couldn't load message info. Try again.",
  noRecipients: "No one else is in this chat yet.",
  deleted: "This message was deleted",
  unavailable: "This message's details are no longer available.",
};
export const DOWNLOAD_FAILED = "Couldn't download this file. Try again.";
export const FILE_TOO_LARGE = (names) =>
  names?.length
    ? `Files must be 10 MB or smaller - ${names.join(", ")} won't be sent.`
    : "Files must be 10 MB or smaller.";
export const TOO_MANY_FILES = (max, added) =>
  added
    ? `You can send up to ${max} files at once - only the first ${added} were added.`
    : `You can send up to ${max} files at once.`;

/* ----- Forwarding ----- */
export const FORWARD = {
  title: (count) => (count > 1 ? "Forward messages" : "Forward message"),
  searchPlaceholder: "Search people or groups",
  noMatch: "No people or groups found",
  none: "No other chats to forward to",
  loadingPeople: "Loading people...",
  peopleFailed: "Couldn't load everyone - only your existing chats are shown",
};

/* ----- The chat ----- */
export const CHAT_MENU = {
  info: "Chat info",
  search: "Search in chat",
  shared: "Media, documents and links",
  pinned: "Pinned messages",
};
export const MUTE = {
  options: [
    { label: "For 1 hour", ms: 60 * 60 * 1000 },
    { label: "For 8 hours", ms: 8 * 60 * 60 * 1000 },
    { label: "For 1 week", ms: 7 * 24 * 60 * 60 * 1000 },
    { label: "Until I turn it back on", ms: null },
  ],
  done: (label) => (label ? `Chat muted - ${label.toLowerCase()}` : "Chat muted until you turn it back on"),
  unmuted: "Notifications turned on",
};
export const STAR = { done: (starred) => (starred ? "Starred" : "Removed from starred") };
export const EMPTY_THREAD = {
  title: "No messages yet",
  text: (canWrite, name) => (canWrite ? `Say hello to ${name || "everyone"}.` : "Nothing has been said here yet."),
};
export const SEARCH_EMPTY = "No messages found";
export const PINNED_SEARCH = { placeholder: "Search pinned messages...", empty: "No pinned messages found" };

/* ----- Composer, pickers and lists ----- */
export const COMPOSER = {
  // Not the chat's name - that's already in the header (and the list, and the details)
  placeholder: "Type a message...",
  documents: "Documents",
};
export const NEW_MEMBERS_NOTE = "New members see messages from when they joined.";
export const PICKERS = {
  teamsSearch: "Search teams...",
  teamsNoMatch: "No team found",
  teamsNone: "You're not in any Care Diary team yet.",
  openChat: "Open chat",
  startChat: "Start chat",
  staffSearch: "Search staff...",
  staffNoMatch: "No staff found",
  directNone: "There's no one you can message yet.",
};
export const CHAT_LIST = {
  searchEmpty: "No chats match your search",
};
export const DETAILS = {
  adminsOnlyValue: "Only admins",
  noAdmins: "No admins",
};

export const PINNED_BAR = {
  title: (n) => (n > 1 ? `${n} pinned messages` : "Pinned message"),
};

/* ----- Typing ----- */
export const TYPING = {
  label: (names) =>
    names.length === 1
      ? `${names[0]} is typing...`
      : names.length === 2
        ? `${names[0]} and ${names[1]} are typing...`
        : "Several people are typing...",
};

/* ----- Groups ----- */
export const GROUP = {
  created: (name) => `${name} created`,
  updated: "Chat updated",
  needsName: "The group needs a name",
  descriptionPlaceholder: "What's this group for?",
  // Editing an existing chat's description (a team chat too)
  editDescriptionPlaceholder: "What's this chat for?",
  adminsOnly: "Only admins can send messages",
  adminsOnlyHint: "Members can still read and react. Only admins can send messages and pin.",
  policyChanged: (adminsOnly) =>
    adminsOnly ? "Only admins can send messages and pin now" : "Everyone can send messages now",
  addMembers: "Add members",
  addButton: (n) => (n ? `Add ${n} ${n === 1 ? "person" : "people"}` : "Add"),
  allAdded: "Everyone who can be added is already in this group.",
  added: (names) => (names.length === 1 ? `${names[0]} added` : `${names.length} people added`),
  teamMembersNote: "Members come from the team. Change them in Teams.",
  makeAdmin: "Make admin",
  removeTitle: (name) => `Remove ${name}?`,
  removeText: (name) => `${name} will no longer see this group or its messages.`,
  removed: (name) => `${name} removed`,
  leaveTitle: (name) => `Leave "${name}"?`,
  leaveText:
    "You'll stop getting its messages and it will leave your chat list. Someone will need to add you back to rejoin.",
  leaveButton: "Leave",
  left: (name) => `You left ${name}`,
  archiveTitle: (name) => `Archive "${name}"?`,
  archiveText: "Nobody will be able to send messages here. Members keep the history, and it can be unarchived later.",
};

/* ----- Why a chat can't be written in ----- */
export const COMPOSER_LOCK = {
  admins_only: "Only group admins can send messages and pin. You can still read and react.",
  archived: "This group is archived. New messages are turned off.",
  read_only_oversight: "You're viewing this chat as an administrator, so you can read but not post.",
  recipient_archived: "This person's profile is archived. You can message them again once it's restored.",
  fallback: "You can read this chat, but you can't send messages.",
};

/* ----- A chat that closes while open ----- */
export const CHAT_CLOSED = {
  removedFromTeam: "You were removed from this team, so its chat has closed.",
  noAccess: "You no longer have access to this chat",
};
